import React, { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { confirmAgentDeposit, createAgentDepositIntent } from '../../api/bookingAgent';

const stripePromise = loadStripe(process.env.REACT_APP_SECRET_KEY as string);

interface AgentInlineDepositProps {
  sessionId: string;
  amount: string;
  currency: string;
  onPaid: () => void;
}

interface DepositFormProps {
  sessionId: string;
  paymentIntentId: string;
  amount: string;
  currency: string;
  onPaid: () => void;
}

function DepositForm({ sessionId, paymentIntentId, amount, currency, onPaid }: DepositFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState('');
  const formattedAmount = Number(amount).toFixed(2);

  const handlePay = async () => {
    if (!stripe || !elements || paying) {
      return;
    }
    setPaying(true);
    setError('');
    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });
      if (confirmError) {
        setError(confirmError.message || 'Payment failed. Please try again.');
        setPaying(false);
        return;
      }
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        await confirmAgentDeposit(sessionId, paymentIntent.id || paymentIntentId);
        onPaid();
        return;
      }
      setError('Payment was not completed. Please try again.');
      setPaying(false);
    } catch (err: any) {
      setError(err?.message || 'Payment failed. Please try again.');
      setPaying(false);
    }
  };

  return (
    <div>
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && (
        <div style={{ fontSize: 12, color: '#c0392b', marginTop: 8 }}>{error}</div>
      )}
      <button
        onClick={handlePay}
        disabled={!stripe || paying}
        style={{
          marginTop: 12, width: '100%', padding: '10px 16px', borderRadius: 20,
          border: 'none', background: paying ? '#c8c1ba' : '#2d2925', color: '#fff',
          fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
          cursor: paying ? 'default' : 'pointer',
        }}
      >
        {paying ? 'Processing…' : `Pay ${currency} ${formattedAmount} & confirm`}
      </button>
    </div>
  );
}

export default function AgentInlineDeposit({ sessionId, amount, currency, onPaid }: AgentInlineDepositProps) {
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntentId, setPaymentIntentId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const formattedAmount = Number(amount).toFixed(2);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    createAgentDepositIntent(sessionId, amount)
      .then((result) => {
        if (!active) {
          return;
        }
        setClientSecret(result.client_secret);
        setPaymentIntentId(result.payment_intent_id);
      })
      .catch(() => {
        if (active) {
          setLoadError('Could not start payment. Please try again.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [sessionId, amount]);

  const options = useMemo(
    () => ({ clientSecret, appearance: { theme: 'stripe' as const } }),
    [clientSecret],
  );

  return (
    <div style={{
      margin: '4px 0 14px 36px', padding: '12px 16px',
      background: '#fdf8f0', borderRadius: 12, border: '1px solid #e8d5b0',
      fontSize: 13, color: '#5d4037',
    }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>
        Deposit required — {currency} {formattedAmount}
      </div>
      {loading && (
        <div style={{ color: '#6b5f54' }}>Preparing secure payment…</div>
      )}
      {loadError && (
        <div style={{ fontSize: 12, color: '#c0392b' }}>{loadError}</div>
      )}
      {!loading && clientSecret && (
        <Elements stripe={stripePromise} options={options}>
          <DepositForm
            sessionId={sessionId}
            paymentIntentId={paymentIntentId}
            amount={amount}
            currency={currency}
            onPaid={onPaid}
          />
        </Elements>
      )}
    </div>
  );
}
